import {
  Avatar,
  AvatarGroup,
  Box,
  Flex,
  Tag,
  TagLabel,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { AiOutlineInfoCircle } from "react-icons/ai";

// Constants
import { PartnerTypes } from "constants/defi";

// Images
import sdIcon from "assets/images/sd.png";

// Types
import { DefiData } from "@customtypes/global";

// Components
import Actions from "./Actions";
import CustomIcon from "@components/Icon";
import { defiCurrencyIconMapping } from "@constants/dappWalletMapping";
import { TextPrimary, TextSecondary } from "theme";
import { xTokenMapping } from "@constants/constants";
import BaseImage from "dapp-sdk-v2/components/Image";
import { getUrlForStaticImage } from "dapp-sdk-v2";
import { formatNumber } from "@utils/globalCommon";

const xToken = process.env.NEXT_PUBLIC_XTOKEN || "";

export const renderAvatarwithSVG = (icon: any, name: string) => {
  if (!icon) {
    return (
      <Avatar bg="primary" name={name} color="white" size="xs" loading="lazy" />
    );
  }
  if (icon) {
    if (icon?.src) {
      const src = getUrlForStaticImage(icon);
      return (
        <Avatar
          bg="transparent"
          name={name}
          size="xs"
          src={src}
          loading="lazy"
        />
      );
    } else {
      return (
        <Box w="24px" h="24px">
          <CustomIcon Icon={icon} height="100%" width="100%" />
        </Box>
      );
    }
  }
};

export const getColumns = (
  hasAPY: boolean,
  defiData: DefiData,
  colorMode: string,
  disableSort: boolean,
  stakingAPY: number
) => [
  {
    Header: "Assets",
    width: "20%",
    Cell: ({ row }: { row: any }) => {
      const item: any = row.original;
      const tags = defiData[item.apiKey]?.tags || [];
      const showIcon = "showToken" in item ? item.showToken : true;
      const isSDPool = item.isSDPool;
      return (
        <Flex gap="4" align="center" flex="1">
          {item.type === PartnerTypes.LENDING_BORROWING ||
          showIcon === false ? (
            <>
              <Box>
                {renderAvatarwithSVG(
                  defiCurrencyIconMapping[xToken.toLowerCase()][colorMode],
                  xTokenMapping[xToken]
                )}
              </Box>
              <Box>
                <Text color="textSecondary" fontSize={14}>
                  {xTokenMapping[xToken]}
                </Text>
                <Flex gap="4px" w="100%" flexWrap="wrap">
                  {tags?.map((tag: string) => (
                    <Tag
                      key={tag}
                      size="sm"
                      bg={tag === "NEW" ? "#EBF8FF" : "#F0FFF4"}
                      color={tag === "NEW" ? "#3182CE" : "#38A169"}
                      mt="1"
                      fontWeight={700}
                      fontSize={10}
                      p="4px 8px"
                      borderRadius="6px"
                    >
                      <TagLabel>{tag}</TagLabel>
                    </Tag>
                  ))}
                </Flex>
              </Box>
            </>
          ) : (
            <>
              <AvatarGroup spacing="-1" size="xs">
                {renderAvatarwithSVG(
                  isSDPool
                    ? sdIcon
                    : defiCurrencyIconMapping[xToken.toLowerCase()][colorMode],
                  isSDPool ? "SD" : xTokenMapping[xToken]
                )}

                {item?.tokenIconKey &&
                  renderAvatarwithSVG(
                    defiCurrencyIconMapping[
                      item?.tokenIconKey?.toLowerCase() || ""
                    ]?.[colorMode],
                    "token"
                  )}
              </AvatarGroup>
              <Box>
                <TextSecondary fontSize={14}>
                  {isSDPool ? "SD" : xTokenMapping[xToken]}{" "}
                  {item.token && `- ${item.token}`}
                </TextSecondary>
                <Flex gap="4px" w="100%" flexWrap="wrap">
                  {tags?.map((tag: string) => (
                    <Tag
                      key={tag}
                      size="sm"
                      bg={tag === "NEW" ? "#EBF8FF" : "#F0FFF4"}
                      color={tag === "NEW" ? "#3182CE" : "#38A169"}
                      mt="1"
                      fontSize={10}
                      fontWeight={700}
                      p="4px 8px"
                      borderRadius="6px"
                    >
                      <TagLabel>{tag}</TagLabel>
                    </Tag>
                  ))}
                </Flex>
              </Box>
            </>
          )}
        </Flex>
      );
    },
  },
  {
    Header: hasAPY ? "APY" : "APR",
    width: "20%",
    accessor: "apy",
    disableSortBy: disableSort,
    Cell: ({ row }: { row: any }) => {
      const item = row.original;
      let APY: string | number = defiData[item.apiKey]?.apy;

      const apyTooltip = defiData[item.apiKey]?.apyTooltip;

      if (item?.should_add_staking_apy) {
        APY = Number(APY) + stakingAPY;
      }

      if (APY) {
        APY = formatNumber(Number(APY), false, 2);
      }

      return (
        <Flex align="center" gap="0.5rem">
          <TextPrimary fontSize="16px" fontWeight="600">
            {APY ? `${APY} %` : "-"}
          </TextPrimary>
          {apyTooltip && apyTooltip.show && (
            <Tooltip label={apyTooltip.text} placement="top">
              <Text ml="4px" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            </Tooltip>
          )}
        </Flex>
      );
    },
    sortType: "basic",
  },
  {
    Header: "TVL",
    width: "15%",
    accessor: "tvlInNumbers",
    disableSortBy: disableSort,
    Cell: ({ row }: { row: any }) => {
      const item = row.original;
      const TVL = defiData[item.apiKey]?.tvl;
      const tvlTooltip = defiData[item.apiKey]?.tvlTooltip;

      return (
        <Flex align="center" gap="0.5rem">
          <TextPrimary fontSize="16px" fontWeight="600">
            {TVL ? `$${TVL}` : "-"}
          </TextPrimary>
          {tvlTooltip && tvlTooltip.show && (
            <Tooltip label={tvlTooltip.text} placement="top">
              <Text ml="4px" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            </Tooltip>
          )}
        </Flex>
      );
    },
    sortType: "basic",
  },
  {
    Header: "Provider",
    width: "20%",
    Cell: ({ row }: { row: any }) => {
      const item = row.original;
      return (
        <Flex align="center" alignItems="center" gap="4px">
          <AvatarGroup spacing="-0.5" size="xs" className="platformIconGroup">
            {item.pairPlatform && (
              <BaseImage
                height={24}
                width={24}
                src={item?.pairPlatform?.icon_key}
                alt={item?.platform?.title || "platform"}
              />
            )}
            <BaseImage
              height={24}
              width={24}
              src={item?.platform?.icon_key}
              alt={item?.platform?.title || "platform"}
            />
          </AvatarGroup>
          <TextSecondary fontSize={14}>{`${
            item.pairPlatform ? `${item.pairPlatform.title} - ` : ""
          }${item?.platform?.title}`}</TextSecondary>
        </Flex>
      );
    },
  },
  {
    Header: " ",
    width: "25%",
    Cell: ({ row }: { row: any }) => {
      const item = row.original;
      return (
        <Actions
          isPaused={defiData[item.apiKey]?.paused}
          id={item.apiKey}
          type={item.type}
          links={item.links}
        />
      );
    },
  },
];
